/**
 * CardImageModal Component
 * Modal for displaying enlarged card images with zoom functionality
 *
 * Features:
 * - Click to open enlarged image
 * - Grey overlay background
 * - Zoom in/out with mouse wheel and buttons
 * - Drag to pan when zoomed
 * - Touch support for mobile devices
 * - Close with escape key or click outside
 * - Body scroll lock when open
 * - Full accessibility support
 * - Responsive design
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import styles from './CardImageModal.module.css'

interface CardImageModalProps {
  imageUrl: string | null
  alt: string
  isOpen: boolean
  onClose: () => void
}

export const CardImageModal: React.FC<CardImageModalProps> = ({
  imageUrl,
  alt,
  isOpen,
  onClose
}) => {
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [imageError, setImageError] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  // Reset zoom, pan, and loading state when modal opens or image changes
  useEffect(() => {
    if (isOpen) {
      setZoom(1)
      setPan({ x: 0, y: 0 })
      setIsLoading(true)
      setImageError(false)
    }
  }, [isOpen, imageUrl])

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${scrollBarWidth}px`
      
      return () => {
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
      }
    }
  }, [isOpen])

  // Focus management and restoration
  useEffect(() => {
    if (isOpen) {
      // Store the element that opened the modal
      previousActiveElement.current = document.activeElement as HTMLElement
      
      // Focus close button after modal is rendered
      const timeoutId = setTimeout(() => {
        closeButtonRef.current?.focus()
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        // Restore focus when modal closes
        previousActiveElement.current?.focus()
      }
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  // Handle mouse wheel zoom with better state management
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setZoom(prevZoom => {
      const newZoom = prevZoom + delta
      return Math.max(0.5, Math.min(3, newZoom))
    })
  }, [])

  // Handle image load
  const handleImageLoad = useCallback(() => {
    setIsLoading(false)
    setImageError(false)
  }, [])

  // Handle image error
  const handleImageError = useCallback(() => {
    setIsLoading(false)
    setImageError(true)
  }, [])

  // Handle mouse down for dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom > 1) {
      e.preventDefault()
      setIsDragging(true)
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
    }
  }, [zoom, pan])

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault()
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }, [isDragging, dragStart])

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Touch support for mobile
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; distance: number } | null>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1 && zoom > 1) {
      // Single touch for panning
      const touch = e.touches[0]
      setTouchStart({
        x: touch.clientX - pan.x,
        y: touch.clientY - pan.y,
        distance: 0
      })
    } else if (e.touches.length === 2) {
      // Two finger pinch for zoom
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      )
      setTouchStart({ x: 0, y: 0, distance })
    }
  }, [zoom, pan])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart) return

    if (e.touches.length === 1 && touchStart.distance === 0 && zoom > 1) {
      // Single touch panning
      e.preventDefault()
      const touch = e.touches[0]
      setPan({
        x: touch.clientX - touchStart.x,
        y: touch.clientY - touchStart.y
      })
    } else if (e.touches.length === 2 && touchStart.distance > 0) {
      // Pinch zoom
      e.preventDefault()
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      )
      const scale = distance / touchStart.distance
      setZoom((prevZoom: number) => Math.max(0.5, Math.min(3, prevZoom * scale)))
      setTouchStart(prev => prev ? { ...prev, distance } : null)
    }
  }, [touchStart, zoom])

  const handleTouchEnd = useCallback(() => {
    setTouchStart(null)
  }, [])

  // Handle click outside to close
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    // A portal still bubbles React events to the component that rendered it, so a click in here
    // would reach a selectable row around the thumbnail and tick it. Enlarging is not selecting.
    e.stopPropagation()
    if (e.target === e.currentTarget) {
      onClose()
    }
  }, [onClose])

  // Same for keys: Enter or Space on the focused close button must not toggle the row behind.
  // Escape is left alone: the document listener above closes the viewer with it.
  const stopKeyBubbling = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') e.stopPropagation()
  }, [])
  const stopBubbling = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
  }, [])

  if (!isOpen || !imageUrl) return null

  return createPortal(
    <div
      ref={modalRef}
      className={styles.modalOverlay}
      onClick={handleOverlayClick}
      onKeyDown={stopKeyBubbling}
      onDoubleClick={stopBubbling}
      role="dialog"
      aria-modal="true"
      aria-label="Card image viewer"
    >
      <div className={styles.modalContent}>
        <button
          ref={closeButtonRef}
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close image viewer"
          type="button"
        >
          ✕
        </button>
        
        <div className={styles.imageContainer}>
          {isLoading && !imageError && (
            <div className={styles.loadingSpinner} aria-live="polite">
              <div className={styles.spinner}></div>
              <p>Loading image...</p>
            </div>
          )}
          
          {imageError ? (
            <div className={styles.errorMessage} role="alert">
              <p>Failed to load image</p>
              <button onClick={onClose} className={styles.errorCloseButton} type="button">
                Close
              </button>
            </div>
          ) : (
            <img
              ref={imageRef}
              src={imageUrl}
              alt={alt}
              className={styles.cardImage}
              style={{
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                opacity: isLoading ? 0 : 1,
                transition: isLoading ? 'none' : 'opacity 0.2s ease-in-out'
              }}
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onLoad={handleImageLoad}
              onError={handleImageError}
              draggable={false}
            />
          )}
        </div>
        
        {!imageError && (
          <div className={styles.controls} role="toolbar" aria-label="Image zoom controls">
            <button
              className={styles.zoomButton}
              onClick={() => setZoom((prev: number) => Math.max(0.5, prev - 0.2))}
              disabled={zoom <= 0.5 || isLoading}
              aria-label="Zoom out"
              type="button"
            >
              Zoom Out
            </button>
            <span className={styles.zoomLevel} aria-live="polite">
              {Math.round(zoom * 100)}%
            </span>
            <button
              className={styles.zoomButton}
              onClick={() => setZoom((prev: number) => Math.min(3, prev + 0.2))}
              disabled={zoom >= 3 || isLoading}
              aria-label="Zoom in"
              type="button"
            >
              Zoom In
            </button>
            <button
              className={styles.resetButton}
              onClick={() => {
                setZoom(1)
                setPan({ x: 0, y: 0 })
              }}
              disabled={isLoading}
              aria-label="Reset zoom and position"
              type="button"
            >
              Reset
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}